<div class="container-fixed">
  <!-- Header -->
  <div class="flex flex-wrap items-center lg:items-end justify-between gap-5 pb-7.5">
    <div class="flex flex-col justify-center gap-2">
      <h1 class="text-xl font-semibold leading-none text-gray-900">
        My Timecard
      </h1>
      <div class="flex items-center gap-2 text-sm font-medium text-gray-600">
        {{ getTodayDisplay() }}
      </div>
    </div>
  </div>

  <!-- Clock In/Out Card -->
  <div class="card mb-5" [ngClass]="{'border-success border-2': isClockedIn()}">
    <div class="card-body">
      <!-- Clocked In Banner -->
      <div *ngIf="isClockedIn()" class="bg-success-light rounded-lg p-4 mb-6 text-center">
        <div class="flex items-center justify-center gap-2 mb-2">
          <span class="relative flex h-3 w-3">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
            <span class="relative inline-flex rounded-full h-3 w-3 bg-success"></span>
          </span>
          <span class="text-success font-semibold text-lg">Currently Clocked In</span>
          <span *ngIf="hasMultipleSessions()" class="badge badge-success-light ml-2">Session {{ todayStatus?.sessionNumber }}</span>
        </div>
        <div class="text-4xl font-bold text-success mb-2 font-mono">
          {{ getElapsedTime() }}
        </div>
        <div class="text-sm text-gray-600">
          Started at {{ formatActualTime(todayStatus?.clockInTime) }}
        </div>
        <!-- Show total hours from previous sessions -->
        <div *ngIf="getTotalSessions() > 1" class="text-sm text-gray-600 mt-1">
          Previous sessions today: {{ (getTotalDayHours() - (todayStatus?.totalHours || 0)).toFixed(2) }}h
        </div>
      </div>

      <div class="flex flex-col lg:flex-row items-center justify-between gap-6">
        <!-- Current Time Display -->
        <div class="text-center lg:text-left">
          <div class="text-4xl font-bold text-gray-900 mb-2 font-mono">
            {{ getCurrentTimeDisplay() }}
          </div>
          <div class="text-sm text-gray-600">Current Time</div>
        </div>

        <!-- Branch Selector -->
        <div class="flex flex-col gap-1" *ngIf="branches.length > 1">
          <label class="form-label text-gray-900">Select Branch</label>
          <select
            class="select select-sm w-48"
            [(ngModel)]="selectedBranchId"
            (change)="onBranchChange()">
            <option *ngFor="let branch of branches" [ngValue]="branch.branchID">
              {{ branch.branchName }}
            </option>
          </select>
        </div>

        <!-- Clock Status & Actions -->
        <div class="flex flex-col items-center gap-3">
          <!-- Status Display -->
          <div class="text-center">
            <!-- Not Clocked In -->
            <div *ngIf="isNotClockedIn()" class="mb-3">
              <span class="badge badge-light text-lg px-4 py-2">Not Clocked In</span>
            </div>

            <!-- Clocked Out -->
            <div *ngIf="isClockedOut()" class="mb-3">
              <span class="badge badge-secondary text-lg px-4 py-2">Completed</span>
              <div class="text-sm text-gray-600 mt-2">
                {{ formatActualTime(todayStatus?.clockInTime) }} - {{ formatActualTime(todayStatus?.clockOutTime) }}
              </div>
              <div class="text-lg font-semibold text-success mt-1">
                {{ todayStatus?.totalHours?.toFixed(2) }} hours
              </div>
              <!-- Multi-session info -->
              <div *ngIf="hasMultipleSessions()" class="text-sm text-primary mt-2">
                Session {{ todayStatus?.sessionNumber }} of {{ getTotalSessions() }} | Total today: {{ getTotalDayHours().toFixed(2) }}h
              </div>
              <div class="text-xs text-gray-500 mt-2">
                <i class="ki-outline ki-information-2 me-1"></i>
                You can clock in again to start a new session
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-3">
            <button
              class="btn btn-lg btn-success"
              [disabled]="!canClockIn() || isClocking"
              (click)="clockIn()">
              <i class="ki-outline ki-entrance-left me-2"></i>
              <span *ngIf="!isClocking">Clock In</span>
              <span *ngIf="isClocking" class="spinner-border spinner-border-sm"></span>
            </button>

            <button
              class="btn btn-lg btn-danger"
              [disabled]="!canClockOut() || isClocking"
              (click)="clockOut()">
              <i class="ki-outline ki-exit-right me-2"></i>
              <span *ngIf="!isClocking">Clock Out</span>
              <span *ngIf="isClocking" class="spinner-border spinner-border-sm"></span>
            </button>
          </div>

          <!-- Scheduled Time Info -->
          <div *ngIf="todayStatus?.scheduledStartTime" class="text-sm text-gray-500 mt-2">
            Scheduled: {{ formatScheduledTime(todayStatus.scheduledStartTime) }} - {{ formatScheduledTime(todayStatus.scheduledEndTime) }}
          </div>

          <!-- No Schedule Warning -->
          <div *ngIf="!todayStatus?.scheduledStartTime && !isLoading" class="mt-2 text-center">
            <span class="badge badge-warning text-sm px-3 py-1">
              <i class="ki-outline ki-information-2 me-1"></i>
              No schedule for today
            </span>
            <div class="text-xs text-gray-500 mt-1">
              You can still clock in if needed
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Timecard History -->
  <div class="card">
    <div class="card-header">
      <h3 class="card-title">Timecard History</h3>
      <div class="flex items-center gap-3">
        <input
          type="date"
          class="input input-sm w-36"
          [(ngModel)]="fromDate"
          (change)="onDateFilterChange()">
        <span class="self-center text-gray-500">to</span>
        <input
          type="date"
          class="input input-sm w-36"
          [(ngModel)]="toDate"
          (change)="onDateFilterChange()">
        <button
          class="btn btn-sm btn-light"
          [disabled]="isLoading || timecardHistory.length === 0"
          (click)="generateReport()"
          title="Export to PDF">
          <i class="ki-outline ki-file-down me-1"></i>
          Export PDF
        </button>
      </div>
    </div>
    <div class="card-body">
      <!-- Loading State -->
      <div *ngIf="isLoading" class="flex justify-center py-10">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>

      <!-- No Data State -->
      <div *ngIf="!isLoading && timecardHistory.length === 0" class="text-center py-10 text-gray-500">
        <i class="ki-outline ki-time text-4xl mb-3"></i>
        <p>No timecard records found for the selected period.</p>
      </div>

      <!-- History Table -->
      <div *ngIf="!isLoading && timecardHistory.length > 0" class="scrollable-x-auto">
        <table class="table table-auto table-border">
          <thead>
            <tr>
              <th class="min-w-[150px]">Date</th>
              <th class="min-w-[120px]">Scheduled</th>
              <th class="min-w-[100px]">Clock In</th>
              <th class="min-w-[100px]">Clock Out</th>
              <th class="min-w-[80px]">Hours</th>
              <th class="min-w-[100px]">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let record of timecardHistory">
              <td>
                <div class="flex flex-col gap-0.5">
                  <span class="font-medium">{{ formatDate(record.workDate) }}</span>
                  <span *ngIf="record.sessionNumber && record.sessionNumber > 1" class="text-xs text-primary">Session {{ record.sessionNumber }}</span>
                </div>
              </td>
              <td>
                <span *ngIf="record.scheduledStartTime && record.scheduledEndTime">
                  {{ formatScheduledTime(record.scheduledStartTime) }} - {{ formatScheduledTime(record.scheduledEndTime) }}
                </span>
                <span *ngIf="!record.scheduledStartTime" class="text-gray-400">-</span>
              </td>
              <td>
                <span class="text-success" *ngIf="record.clockInTime">
                  {{ formatActualTime(record.clockInTime) }}
                </span>
                <span *ngIf="!record.clockInTime" class="text-gray-400">-</span>
              </td>
              <td>
                <span class="text-danger" *ngIf="record.clockOutTime">
                  {{ formatActualTime(record.clockOutTime) }}
                </span>
                <span *ngIf="!record.clockOutTime" class="text-gray-400">-</span>
              </td>
              <td>
                <span *ngIf="record.totalHours !== null && record.totalHours !== undefined">
                  {{ record.totalHours | number:'1.2-2' }}h
                </span>
                <span *ngIf="record.totalHours === null || record.totalHours === undefined" class="text-gray-400">-</span>
              </td>
              <td>
                <span class="badge" [ngClass]="{
                  'badge-success': record.status === 'Completed',
                  'badge-warning': record.status === 'Active',
                  'badge-info': record.status === 'Edited'
                }">
                  {{ record.status }}
                </span>
                <span *ngIf="record.isManualEntry" class="badge badge-light ml-1" title="Manual Entry">
                  <i class="ki-outline ki-pencil text-xs"></i>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div *ngIf="totalRecords > pageSize" class="mt-4">
        <app-paginator
          [length]="totalRecords"
          [pageSize]="pageSize"
          [currentPage]="currentPage"
          (pageChange)="onPageChange($event)"
          (pageSizeChange)="onPageSizeChange($event)">
        </app-paginator>
      </div>
    </div>
  </div>
</div>
